/* This file is auto-generated. Manual changes will be overridden */
import { appendToUrl, assertValidUrl, defaultRetryableStatusCodes, defaultRetryBackoff, delay, HttpError, normalizeHeaders, omitUndefined, } from './utils.js';
import { ResponseType, } from './types.js';
export default class MpcApiClient {
    #base;
    #fetch;
    #onRequest;
    #onResponse;
    #failOnHttpError;
    #headers;
    #fetchOptions;
    #retry;
    /**
     * @param opts
     * @param opts.baseUrl Base URL of the API, that all paths are appended to
     * @param [opts.headers] Default headers that are used for every request
     * @param [opts.fetch] Spec compliant fetch to be used instead of global fetch for all requests
     * @param [opts.fetchOptions] Default values for fetch options that will be supplied and merged with options provided per request
     * @param [opts.onRequest] Hook that allows modifying the Request object before the request is send
     * @param [opts.failOnHttpError] Throw if the server responds with a non-200 status code (defaults to true)
     * @param [opts.retry] Set of options to control the retry behavior of the client, set retry.statusCode to an empty array to disable retrying
     */
    constructor({ headers = Object.create(null), fetch = globalThis.fetch.bind(globalThis), fetchOptions, baseUrl, onRequest = async (request) => request, onResponse = async (response) => response, failOnHttpError = true, retry = {}, }) {
        assertValidUrl(baseUrl);
        this.#base = String(baseUrl);
        this.#fetch = fetch;
        this.#onRequest = onRequest;
        this.#onResponse = onResponse;
        this.#failOnHttpError = failOnHttpError;
        this.#headers = normalizeHeaders(headers);
        this.#fetchOptions = fetchOptions;
        this.#retry = {
            attempts: 3,
            backoff: defaultRetryBackoff,
            ...retry,
            statusCodes: new Set(retry?.statusCodes ?? defaultRetryableStatusCodes),
        };
    }
    #request = async (params) => {
        const { path, method, body, headers, responseAs, contentType, retryAttempt = 0, query, ...fetchOptions } = params;
        const url = new URL(appendToUrl(this.#base, path));
        if (query) {
            url.search = new URLSearchParams(omitUndefined(query)).toString();
        }
        const allHeaders = {
            ...this.#headers,
            ...normalizeHeaders(headers),
            ...(contentType && { 'Content-Type': contentType }),
        };
        const init = {
            ...this.#fetchOptions,
            ...fetchOptions,
            headers: allHeaders,
            method,
            body,
        };
        const request = await this.#onRequest(new Request(url, init));
        const _response = await this.#fetch(request);
        const response = await this.#onResponse(_response);
        if (this.#retry.statusCodes.has(response.status) && retryAttempt < this.#retry.attempts) {
            const timeout = await this.#retry.backoff({ attempt: retryAttempt, status: response.status });
            await delay(timeout);
            return this.#request({ ...params, retryAttempt: retryAttempt + 1 });
        }
        if (this.#failOnHttpError && responseAs !== ResponseType.Response && !response.ok) {
            let message = `Request to ${url} failed with status ${response.status} (${response.statusText})`;
            if (retryAttempt > 0) {
                message += ` after ${retryAttempt} attempts`;
            }
            throw new HttpError(message, response);
        }
        if (responseAs === ResponseType.Json) {
            return (response.status === 204 ? undefined : response.json());
        }
        if (responseAs === ResponseType.Text) {
            return response.text();
        }
        return response;
    };
    #internal = {
        sign: ({ params: body, responseAs, ...fetchOptions }) => {
            return this.#request({
                ...fetchOptions,
                path: '/api/v1/sign',
                method: 'post',
                responseAs: (responseAs ?? ResponseType.Json),
                contentType: 'application/json',
                body: JSON.stringify(body),
            });
        },
        getStatus: ({ responseAs, ...fetchOptions } = {}) => {
            return this.#request({
                ...fetchOptions,
                path: '/status',
                method: 'get',
                responseAs: (responseAs ?? ResponseType.Json),
            });
        },
        auth: {
            getLoginChallenge: ({ responseAs, ...fetchOptions } = {}) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/auth/challenge/login',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                });
            },
            getRegistrationChallenge: ({ responseAs, ...fetchOptions } = {}) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/auth/challenge/register',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                });
            },
            login: ({ params: body, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/auth/login',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            logout: ({ responseAs, ...fetchOptions } = {}) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/auth/logout',
                    method: 'get',
                    responseAs: (responseAs ?? ResponseType.Json),
                });
            },
            whoami: ({ responseAs, ...fetchOptions } = {}) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/auth/whoami',
                    method: 'get',
                    responseAs: (responseAs ?? ResponseType.Json),
                });
            },
        },
        messages: {
            create: ({ params: { conversationId, ...body }, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: `/api/v1/messages/${encodeURIComponent(conversationId)}`,
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            take: ({ params: { notEqual, conversationId }, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: `/api/v1/messages/${encodeURIComponent(conversationId)}`,
                    method: 'delete',
                    responseAs: (responseAs ?? ResponseType.Json),
                    query: { 'not-equal': notEqual },
                });
            },
        },
        invites: {
            create: ({ params: body, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/invites',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            accept: ({ params: { inviteId, ...body }, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: `/api/v1/invites/${encodeURIComponent(inviteId)}`,
                    method: 'delete',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
        },
        wallets: {
            createUserKeyShare: ({ params: body, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/user-key-shares',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            getUserKeyShares: ({ responseAs, ...fetchOptions } = {}) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/user-key-shares',
                    method: 'get',
                    responseAs: (responseAs ?? ResponseType.Json),
                });
            },
            initializeMultiPartyOperation: ({ params: { scheme, ...body }, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: `/api/v1/user-key-shares/${encodeURIComponent(scheme)}/rooms`,
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            initializeKeyGeneration: ({ params: body, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/wallets/keygens',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            create: ({ params: body, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: '/api/v1/wallets',
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
            exportPrivateKey: ({ params: { scheme, ...body }, responseAs, ...fetchOptions }) => {
                return this.#request({
                    ...fetchOptions,
                    path: `/api/v1/wallets/${encodeURIComponent(scheme)}/export`,
                    method: 'post',
                    responseAs: (responseAs ?? ResponseType.Json),
                    contentType: 'application/json',
                    body: JSON.stringify(body),
                });
            },
        },
    };
    api = {
        /** Sign a payload for a derivation path and scheme */
        sign: Object.assign((params) => this.#internal.sign({ params }), {
            with: (requestParams) => ({
                request: (params) => this.#internal.sign({ ...requestParams, params }),
            }),
        }),
        /** Get the status of the server */
        getStatus: Object.assign(() => this.#internal.getStatus(), {
            with: (requestParams) => ({
                request: () => this.#internal.getStatus(requestParams),
            }),
        }),
        auth: {
            /** Receive the login challenge */
            getLoginChallenge: Object.assign(() => this.#internal.auth.getLoginChallenge(), {
                with: (requestParams) => ({
                    request: () => this.#internal.auth.getLoginChallenge(requestParams),
                }),
            }),
            /** Receive the registration options and challenge */
            getRegistrationChallenge: Object.assign(() => this.#internal.auth.getRegistrationChallenge(), {
                with: (requestParams) => ({
                    request: () => this.#internal.auth.getRegistrationChallenge(requestParams),
                }),
            }),
            /** Login or register the user with the WebAuthn attestation or assertion response from the local authenticator */
            login: Object.assign((params) => this.#internal.auth.login({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.auth.login({ ...requestParams, params }),
                }),
            }),
            /** End the current session */
            logout: Object.assign(() => this.#internal.auth.logout(), {
                with: (requestParams) => ({
                    request: () => this.#internal.auth.logout(requestParams),
                }),
            }),
            /** Return meta information about the authenticated passkey */
            whoami: Object.assign(() => this.#internal.auth.whoami(), {
                with: (requestParams) => ({
                    request: () => this.#internal.auth.whoami(requestParams),
                }),
            }),
        },
        messages: {
            /** Create a message for a conversation */
            create: Object.assign((params) => this.#internal.messages.create({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.messages.create({ ...requestParams, params }),
                }),
            }),
            /** Deletes the current message for a conversation and returns it */
            take: Object.assign((params) => this.#internal.messages.take({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.messages.take({ ...requestParams, params }),
                }),
            }),
        },
        invites: {
            /** Create an invite for a wallet */
            create: Object.assign((params) => this.#internal.invites.create({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.invites.create({ ...requestParams, params }),
                }),
            }),
            /** Accept an invite */
            accept: Object.assign((params) => this.#internal.invites.accept({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.invites.accept({ ...requestParams, params }),
                }),
            }),
        },
        wallets: {
            /** Save the client's key share in the database */
            createUserKeyShare: Object.assign((params) => this.#internal.wallets.createUserKeyShare({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.wallets.createUserKeyShare({ ...requestParams, params }),
                }),
            }),
            /** Return the users key shares */
            getUserKeyShares: Object.assign(() => this.#internal.wallets.getUserKeyShares(), {
                with: (requestParams) => ({
                    request: () => this.#internal.wallets.getUserKeyShares(requestParams),
                }),
            }),
            /** Create a room for a multi party operation such as signing or exporting a key and return the user key share for the given scheme */
            initializeMultiPartyOperation: Object.assign((params) => this.#internal.wallets.initializeMultiPartyOperation({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.wallets.initializeMultiPartyOperation({ ...requestParams, params }),
                }),
            }),
            /** Create key generation rooms and initialize the key generation */
            initializeKeyGeneration: Object.assign((params) => this.#internal.wallets.initializeKeyGeneration({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.wallets.initializeKeyGeneration({ ...requestParams, params }),
                }),
            }),
            /** Create a new wallet */
            create: Object.assign((params) => this.#internal.wallets.create({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.wallets.create({ ...requestParams, params }),
                }),
            }),
            /** export the extended private key of the wallet */
            exportPrivateKey: Object.assign((params) => this.#internal.wallets.exportPrivateKey({ params }), {
                with: (requestParams) => ({
                    request: (params) => this.#internal.wallets.exportPrivateKey({ ...requestParams, params }),
                }),
            }),
        },
    };
}
